import { apiFetch, withAuth } from "../common/api";

const HOST = import.meta.env.VITE_API_HOST;

/**
 * @async
 * @param {object} accessToken
 * @param {string} path
 * @returns {Promise<{mediaType: string, mediaBlobUrl: string}>}
 * @throws {Error} Throws an error if the fetch operation fails or if the media type is unsupported.
 * @description Fetches media from the given endpoint and returns an object containing the media type and URL.
 */
export async function fetchMedia(accessToken, path) {
  console.log(`Fetching media content from ${path}`);
  const response = await apiFetch(
    `${HOST}/api/v1/media-content/by-user`,
    withAuth(accessToken),
  );

  if (!response.ok) {
    throw new Error("Network response was not ok");
  }

  const contentType = response.headers.get("content-type");
  let mediaType;

  if (contentType.startsWith("image/")) {
    mediaType = "image";
  } else if (contentType.startsWith("video/")) {
    mediaType = "video";
  } else {
    throw new Error("Unsupported media type");
  }

  const blob = await response.blob();
  const mediaBlobUrl = URL.createObjectURL(blob);

  return { mediaType, mediaBlobUrl };
}

/**
 * @async
 * @param {object} accessToken
 * @returns {Promise<Array<MediaContent>>}
 * @throws {Error} Throws an error if the fetch operation fails or if the media type is unsupported.
 * @description Fetches media from the given endpoint and returns an object containing the media type and URL.
 */
export async function fetchAll(accessToken) {
  const response = await apiFetch(
    `${HOST}/api/v1/media-content`,
    accessToken ? withAuth(accessToken) : undefined,
  );
  const data = await response.json();
  return data;
}

/**
 * @async
 * @param {object} accessToken
 * @param {{location_id: number, day_type: number, file_path: string}} req
 * @returns {Promise<undefined>}
 * @throws {Error} Throws an error if the fetch operation fails or if the media type is unsupported.
 * @description Fetches media from the given endpoint and returns an object containing the media type and URL.
 */
export async function saveOrUpdate(accessToken, req) {
  const opts = {
    method: "POST",
    headers: { "Content-Type": "application/json" },
    body: JSON.stringify(req),
  };
  await apiFetch(
    `${HOST}/api/v1/media-content`,
    accessToken ? withAuth(accessToken, opts) : opts,
  );
}

/**
 * @async
 * @param {object} accessToken
 * @returns {Promise<Array<string>>}
 */
export async function fetchAllMediaFileNames(accessToken) {
  const response = await apiFetch(
    `${HOST}/api/v1/media-content/files`,
    accessToken ? withAuth(accessToken) : undefined,
  );
  const data = await response.json();
  return data;
}

/**
 * @param {object} accessToken
 * @returns {string}
 */
export async function initUpdatesSubscription(accessToken) {
  const resp = await apiFetch(
    `${HOST}/api/v1/media-content/updates/subscription`,
    withAuth(accessToken),
  );
  if (resp.status != 200) {
    throw await resp.text();
  }
  return await resp.json();
}

/**
 * @param {string} eventSourceId
 * @returns {EventSource}
 */
export async function subscribeToUpdates(eventSourceId) {
  return new EventSource(
    `${HOST}/api/v1/media-content/updates/${eventSourceId}`,
  );
}

/**
 * @async
 * @param {object} accessToken
 * @param {File} file
 * @returns {Promise<{filename: string, message: string}>}
 */
export async function uploadFile(accessToken, file) {
  const formData = new FormData();
  formData.append("file", file);

  const response = await apiFetch(
    `${HOST}/api/v1/media-content/upload`,
    withAuth(accessToken, {
      method: "POST",
      body: formData,
    }),
  );

  if (!response.ok) {
    const error = await response.json();
    throw new Error(error.detail || "Upload failed");
  }

  return await response.json();
}

/**
 * @async
 * @param {object} accessToken
 * @param {string} filePath
 * @returns {Promise<{message: string}>}
 */
export async function deleteFile(accessToken, filePath) {
  const response = await apiFetch(
    `${HOST}/api/v1/media-content/files/${filePath}`,
    withAuth(accessToken, {
      method: "DELETE",
    }),
  );

  if (!response.ok) {
    const error = await response.json();
    throw new Error(error.detail || "Delete failed");
  }

  return await response.json();
}

/**
 * @async
 * @param {object} accessToken
 * @param {string} filePath
 * @param {string} newName
 * @returns {Promise<{message: string, new_name: string}>}
 */
export async function renameFile(accessToken, filePath, newName) {
  const response = await apiFetch(
    `${HOST}/api/v1/media-content/files/${filePath}`,
    withAuth(accessToken, {
      method: "PUT",
      headers: { "Content-Type": "application/json" },
      body: JSON.stringify({ new_name: newName }),
    }),
  );

  if (!response.ok) {
    const error = await response.json();
    throw new Error(error.detail || "Rename failed");
  }

  return await response.json();
}

/**
 * @async
 * @param {object} accessToken
 * @returns {Promise<{customer_id: number, order: OnlineOrder | null, customer_phone: string | null} | null>}
 * @description Fetches the current active order for the manager's location
 */
export async function fetchActiveOrder(accessToken) {
  const response = await apiFetch(
    `${HOST}/api/v1/media-content/active-order`,
    withAuth(accessToken),
  );

  if (!response.ok) {
    if (response.status === 404) {
      return null;
    }
    throw new Error("Failed to fetch active order");
  }

  return await response.json();
}
